import React, { useState, useEffect } from 'react';
import URLSearchParams from '@ungap/url-search-params';

export type ThemePayload = {
  methodName?: string;
  colorScheme?: string;
  handled?: boolean;
};

export type Intercept = (payload: ThemePayload) => ThemePayload;

declare global {
  interface IWindow extends Window {
    interceptCallJavaScript: (fn: Intercept) => void;
  }
}

export enum ThemeOptions {
  light = 'LIGHT',
  dark = 'DARK',
  vault = 'VAULT',
}

type ThemeOptionsKey = keyof typeof ThemeOptions;
type UpdateFunction = (newTheme: string) => void;

export type ThemeContextValues = {
  theme: ThemeOptions;
  updateTheme: UpdateFunction;
  isVault: boolean;
};

export const ThemeContext = React.createContext<ThemeContextValues>({
  theme: ThemeOptions.light,
  updateTheme: null,
  isVault: false,
});

export const getInitialThemeMode = (
  url = window.location.search || window.location.href
): ThemeOptions => {
  if (!url) return ThemeOptions.light;
  const [, queryParams] = url.split('?');
  const urlParams = new URLSearchParams(queryParams);
  const themeRequested = urlParams.get('colorScheme') as ThemeOptionsKey;
  return ThemeOptions[themeRequested] || ThemeOptions.light;
};

export interface IProps {
  children?: React.ReactNode;
  urlParams?: string;
}

export const Theme: React.FC<IProps> = (props: IProps) => {
  const { children, urlParams } = props;
  const initialThemeValue = getInitialThemeMode(urlParams);
  const [theme, setTheme] = useState(initialThemeValue);

  const updateTheme = React.useCallback(
    newTheme => {
      const nextTheme = ThemeOptions[newTheme] || theme;
      setTheme(nextTheme);
    },
    [theme, setTheme]
  );

  const [isVault, setIsVault] = useState(theme === ThemeOptions.vault);
  useEffect(() => {
    setIsVault(theme === ThemeOptions.vault);
  }, [theme]);

  useEffect(() => {
    (window as IWindow & typeof globalThis).interceptCallJavaScript(
      (payload: ThemePayload) => {
        const { methodName, colorScheme } = payload;
        if (methodName === 'updateColorScheme') {
          updateTheme(colorScheme);
          return { ...payload, handled: true };
        }
        return payload;
      }
    );
  }, [updateTheme]);

  return (
    <ThemeContext.Provider value={{ theme, updateTheme, isVault }}>
      {children}
    </ThemeContext.Provider>
  );
};
